import { Component, effect, ChangeDetectionStrategy } from '@angular/core';
import { BackendService } from '@app/@shared/backend.service';
import { ProjectService } from '@app/shell/header/header.project.service';
import { FLAIRR_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { TableExportService } from '@shared/table-export.service';
import type { ExportColumn, ExportRow } from '@shared/table-export.service';
import {
  normalizeNumericTableRows,
  isNumericTableColumn,
  SMALL_TABLE_FILTER_BUTTON_PROPS,
} from '@shared/table-filter.utils';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { FilterMetadata } from '@openng/optimus-ui/api';
import type { Table, TableFilterEvent } from '@openng/optimus-ui/table';

type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

interface QcData {
  [key: string]: {
    desc: string;
    cols: ExportColumn[];
    data: ExportRow[];
  };
}

@Component({
  // This component is activated through routing and is not used as an element selector.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'flairr-stats',
  templateUrl: './flairr-stats.component.html',
  styleUrl: './flairr-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlairrStatsComponent {
  isLoading = false;
  public projectViewData: QcData = {};
  public selectedProjectID: string = '';
  activeAnalysisKey = '';
  statsFilters: TableFilters = this.createTableFilters();
  globalFilter = '';
  filteredStatsRows: ExportRow[] = [];
  exportMessage = '';
  readonly pageSizes = [10, 20, 50, 200, 500];
  readonly filterButtonProps = SMALL_TABLE_FILTER_BUTTON_PROPS;

  // Navigation configuration - flairr-stats is for FLAIRR analysis only
  get navConfig(): NavConfig {
    return FLAIRR_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return 'flairr';
  }

  get globalFilterFields(): string[] {
    return this.activeColumns.map((column) => column.data);
  }

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private exportService: TableExportService,
  ) {
    effect(() => {
      this.setProject();
    }, {});
  }

  // Haplotype / novel allele charts

  // Project / locus selection

  setProject() {
    if (this.projectService.selectedProjectDetails() !== null) {
      const projectDetails = this.projectService.selectedProjectDetails();
      this.selectedProjectID = projectDetails ? projectDetails.project_id : '';

      console.log('setting project: ', this.selectedProjectID);

      this.isLoading = true;
      console.log('getting project view data');
      this.backendService.getProjectQCData(this.selectedProjectID, 'FLAIRR-Seq').subscribe((response: any) => {
        if (response.status === 200) {
          this.isLoading = false;
          const qcData = response.reply as QcData;
          this.projectViewData = Object.fromEntries(
            Object.entries(qcData).map(([key, analysis]) => [
              key,
              {
                ...analysis,
                data: normalizeNumericTableRows(analysis.cols, analysis.data),
              },
            ]),
          );
          if (Object.keys(this.projectViewData).length > 0) {
            this.selectAnalysis(Object.keys(this.projectViewData)[0]);
          } else {
            this.activeAnalysisKey = '';
            this.filteredStatsRows = [];
          }
        } else {
          this.isLoading = false;
        }
      });
    }
  }

  onSelectedAnalysis(event: any) {
    this.selectAnalysis(event.target.value);
  }

  selectAnalysis(key: string) {
    this.activeAnalysisKey = key;
    this.statsFilters = this.createTableFilters();
    this.globalFilter = '';
    this.filteredStatsRows = this.activeRows;
    this.exportMessage = '';
  }

  get activeColumns(): ExportColumn[] {
    return this.projectViewData[this.activeAnalysisKey]?.cols ?? [];
  }

  get activeRows(): ExportRow[] {
    return this.projectViewData[this.activeAnalysisKey]?.data ?? [];
  }

  isNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.activeRows);
  }

  onGlobalFilterChange(event: Event, table: Table): void {
    this.globalFilter = (event.target as HTMLInputElement).value;
    table.filterGlobal(this.globalFilter, FilterMatchMode.CONTAINS);
  }

  onStatsTableFilter(event: TableFilterEvent): void {
    this.filteredStatsRows = (event.filteredValue as ExportRow[] | undefined) ?? this.activeRows;
  }

  async copyStatsTable(): Promise<void> {
    await this.runExport('copy', () => this.exportService.copy(this.activeColumns, this.filteredStatsRows));
  }

  async exportStatsExcel(): Promise<void> {
    await this.runExport('Excel', () =>
      this.exportService.exportExcel(this.activeColumns, this.filteredStatsRows, 'flairr-qc-statistics'),
    );
  }

  async exportStatsPdf(): Promise<void> {
    await this.runExport('PDF', () =>
      this.exportService.exportPdf(this.activeColumns, this.filteredStatsRows, 'flairr-qc-statistics'),
    );
  }

  private async runExport(label: string, action: () => Promise<void>): Promise<void> {
    this.exportMessage = '';
    try {
      await action();
      this.exportMessage = label === 'copy' ? 'Table copied to clipboard.' : `${label} export downloaded.`;
    } catch (error) {
      this.exportMessage = `Could not complete ${label === 'copy' ? 'copy' : `${label} export`}.`;
      console.error(`Failed to complete ${label} for FLAIRR QC data.`, error);
    }
  }

  private createTableFilters(): TableFilters {
    return { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
  }
}
